import React, { useRef, useEffect, useState, useMemo } from "react";
import { withRouter } from "react-router-dom";
import { matchRoutes } from "react-router-config";
import { connect } from "react-redux";

import { asideRepositoryNavigationByModule } from "../Repository/navigation";

import styles from "./Repository.module.sass";

import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    downloadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    setDataFileStatusAction,
    deleteFilePermanentlyAction,
    deleteInvalidFilePermanentlyAction,
    hideFileAction,
    downloadFileAction,
    reprocessRepositoryAction,
    genericSearchAction,
    getFindingDetails,
    reprocessFileAction,
    getDownloadTemplateData,
    deleteGeneratorFileEntryAction,
    forceGenerateFileAction,
} from "../../store/actions";

import proxy from "../../routes/proxy";

import { changeDeepObject } from "../../utils";
import { resolveOverviewLayoutLinks } from "../../utils/resolveOverviewLayoutLinks";
import {
    hasModule,
    redirectNotValid,
    buildStaticLinks,
    setAccount,
    applyThemeWithPriority,
} from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import { alias } from "../../utils/queryUtils";
import useUpdateThemes from "../../hooks/useUpdateThemes";

import {
    controllerQueries,
    generateChildren,
    getQueryResultsArray,
    onClickConfiguratorRepository,
    repoRedirectValidations,
    GlobalPageLayout,
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    buildDisplayComponents,
    buildHeaderComponents,
    headerTitle,
    getDownloadableQueries,
    generateNavigation,
    buildNavProps,
    headerFileTypesPresent,
} from "../utils/utils";

const RepositoryNewLogic = (props) => {
    const {
        pushModal,
        modules,
        fetch,
        menu,
        match,
        route,
        location,
        history,
        reload,
        matchParams,
        projectId,
        locale,
        fetchResults,
        fetchStatus,
        appContext,
        auth,
        userInformation,
        i18n,
    } = useControllerInitialization(props);

    let { fiscalYear, importId, section, fileType } = matchParams;
    const filesList = useRef([]);

    // ViewMode state management for Source/Legal toggle
    const isInitialMount = useRef(true);
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const initialViewMode = searchParams.get("viewMode") || "source";
    const [viewMode, setViewMode] = useState(initialViewMode);
    const previousViewMode = useRef(viewMode);

    const {
        menuOrganizationProject,
        organizationProjects,
        queryStatus,
        projectFiscalYears,
        project,
        fiscalYears,
        fiscalYearsWithImportIds,
        importIdsFromUrlFiscalYear,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus: repositoryQueryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: RepositoryNewLogic,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
        viewMode,
    });

    const { navData, importIdToUse, routeQueries, params, firstModuleFromProject } =
        buildGenericMenu(
            modules,
            fetchResults,
            locale,
            fiscalYear,
            projectId,
            importId,
            project,
            location,
            matchParams,
            route,
        );

    const { hasSource, hasLegal } = headerFileTypesPresent(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        {},
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId
    );

    useEffect(() => {
        if (searchParams.has("viewMode")) {
            setViewMode(searchParams.get("viewMode"));
        } else {
            setViewMode(!hasSource && hasLegal ? "legal" : "source");
        }
    }, [searchParams, hasSource, hasLegal]);

    // Handle viewMode changes and URL sync
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            previousViewMode.current = viewMode;

            // If no viewMode in URL, add it automatically
            if (viewMode && !searchParams.get("viewMode")) {
                const currentSearchParams = new URLSearchParams(location.search);
                const resolvedViewMode = !hasSource && hasLegal ? "legal" : viewMode;
                currentSearchParams.set("viewMode", resolvedViewMode);
                const newSearch = currentSearchParams.toString();
                const newUrl = `${location.pathname}?${newSearch}`;
                history.replace(newUrl);
            }
            return;
        }

        // Only process if viewMode really changed
        if (viewMode && previousViewMode.current !== viewMode && history && location) {
            previousViewMode.current = viewMode;

            // Add viewMode to URL as query parameter
            const currentSearchParams = new URLSearchParams(location.search);
            currentSearchParams.set("viewMode", !hasSource && hasLegal ? "legal" : viewMode);
            currentSearchParams.delete("filter");
            currentSearchParams.delete("resetFilters");
            currentSearchParams.delete("sort");
            currentSearchParams.delete("page");
            currentSearchParams.delete("pageSize");
            const newSearch = currentSearchParams.toString();
            //const newUrl = `${location.pathname}?${newSearch}`;
            const pathnameWithFull = `${locale ? `/${locale}` : "/en"}/${projectId}/${fiscalYear}/full/repository/${section}`;
            const newUrl = `${pathnameWithFull}?${newSearch}`;

            // Update URL - DataLoader will automatically fetch new queries when location.search changes
            history.replace(newUrl);
        }
    }, [
        viewMode,
        location,
        history,
        searchParams,
        fiscalYear,
        projectId,
        section,
        locale,
        hasSource,
        hasLegal,
    ]);

    const handleViewModeChange = (newValue) => {
        setViewMode(newValue);
    };

    const { loading = true, success = false } = repositoryQueryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    // Apply theme with priority logic: Project > Organization > Default
    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "Repository");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const genericOverviewRouteQuery = routeQueries.find((q) => q.resource === "generic_overview");
    const genericOverviewQueryName = genericOverviewRouteQuery
        ? alias({ ...genericOverviewRouteQuery, locale })
        : "";

    const {
        repositoryStatus = { status: 1 },
        getTotalExportLines = "",
        genericOverview,
    } = getQueryResultsArray(fetchResults, [
        {
            resultName: "repositoryStatus",
            queryName: `${locale}_repository_status_fiscalYear_${fiscalYear}_projectId_${projectId}${viewMode ? `_viewMode_${viewMode}` : ""}`,
        },
        {
            resultName: "getTotalExportLines",
            queryName: `${locale}_get_limit_export`,
        },
        {
            resultName: "genericOverview",
            queryName: genericOverviewQueryName,
        },
    ]);

    const getLayout = resolveOverviewLayoutLinks(
        JSON.parse(genericOverview || "[]"),
        hasModule(modules, "new_navigation"),
        viewMode,
    );

    changeDeepObject(getLayout, ["component", "cards"], {
        SubmitSalesInfo: { projectData: projectFiscalYears },
    });

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const displayComponents = buildDisplayComponents(route, getLayout).map((comp) => {
        // Inject viewMode and fiscal year props into RepositoryTabs component
        if (
            comp.parsedName === "RepositoryTabs" ||
            comp.component?.displayName === "RepositoryTabs"
        ) {
            return {
                ...comp,
                props: {
                    ...comp.props,
                    viewMode,
                    onViewModeChange: handleViewModeChange,
                    fiscalYear,
                    fiscalYears: fiscalYearsWithImportIds,
                    history,
                    location,
                },
            };
        }
        return comp;
    });

    const children = generateChildren({
        displayComponents,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        modules,
        fetch,
        pushModal,
        reload,
        onClickConfigurator: onClickConfiguratorRepository,
    });

    const title = headerTitle(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        fiscalYear,
        importId,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: RepositoryNewLogic,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        viewMode,
    );

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    // function to make the correct redirects in the repository
    const repositoryRedirect = repoRedirectValidations({
        redirect,
        fiscalYear,
        importId,
        fiscalYears,
        appContext,
        match,
        matchParams,
        importIdToUse,
        firstModuleFromProject,
        section,
        fileType,
        queryStatus,
        setAccount,
        project,
        locale,
        history,
        proxy,
        projectId,
    });

    if (repositoryRedirect) {
        return repositoryRedirect;
    }

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: {
            ...params,
            fiscalYear,
            importId,
            ...(viewMode && { viewMode }),
        },
        repositoryStatus,
        getTotalExportLines,
        staticLinks,
        styles,
        organizationProjects,
    };

    return (
        LoadingComponent || (
            <GlobalPageLayout
                styles={styles}
                globalLayoutProps={globalLayoutProps}
                modules={modules}
                headerProps={{
                    filesList,
                    reportsAction: true,
                    fiscalYears: fiscalYearsWithImportIds,
                    importIds: appContext === "repository" ? "" : importIdsFromUrlFiscalYear,
                    isRepository: appContext === "repository",
                    externalViewToggle: appContext === "repository",
                    downloadableQueries,
                    hasLegal,
                    hasSource,
                    title: title ? i18n`${title}` : i18n`${params.section || ""}`,
                    totalLinesCsv: getTotalExportLines,
                    headerComponents: headerComponents,
                    titleBarActions:
                        headerComponents.filter((c) => !c.inline).length > 0
                            ? headerComponents
                                  .filter((c) => !c.inline)
                                  .map((headerComponent, index) => (
                                      <headerComponent.Component
                                          key={`titlebar-action-${index}`}
                                          filesList={filesList}
                                          pushModal={pushModal}
                                          i18n={i18n}
                                          uploadFiles={uploadAction}
                                          reload={reload}
                                          projectId={projectId}
                                          user={userInformation}
                                          endPointToRequest={
                                              route?.headerComponentConfig?.endPointToRequest || ""
                                          }
                                          {...headerComponent.props}
                                      />
                                  ))
                            : undefined,
                }}
                headerComponentsMapper={() => (headerComponentProperties) => (
                    <headerComponentProperties.Component
                        key="generation"
                        filesList={filesList}
                        pushModal={pushModal}
                        i18n={i18n}
                        uploadFiles={uploadAction}
                        reload={reload}
                        projectId={projectId}
                        user={userInformation}
                        endPointToRequest={route?.headerComponentConfig?.endPointToRequest || ""}
                        {...headerComponentProperties.props}
                    />
                )}
                fetch={fetch}
                route={route}
                matchParams={matchParams}
                menu={menu}
                // This controller does not need to have isPageLoading
                isLoadingContent={() => false}
            >
                {children}
            </GlobalPageLayout>
        )
    );
};

RepositoryNewLogic.queries = (args, userModulesByProject) => {
    return controllerQueries(args, userModulesByProject);
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        getFindingDetails,
        setDataFileStatus: setDataFileStatusAction,
        deleteFilePermanently: deleteFilePermanentlyAction,
        deleteGeneratorFileEntry: deleteGeneratorFileEntryAction,
        deleteInvalidFilePermanently: deleteInvalidFilePermanentlyAction,
        hideFile: hideFileAction,
        downloadFile: downloadFileAction,
        reprocessRepository: reprocessRepositoryAction,
        uploadFiles: uploadAction,
        download: downloadAction,
        submitAuditNote: auditNoteSubmit,
        genericSearchAction,
        reprocessFiles: reprocessFileAction,
        getDownloadTemplateData,
        forceGenerateFile: forceGenerateFileAction,
    },
)(withRouter(RepositoryNewLogic));
